import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Supply = {
  id: string;
  name: string;
  size: string;
  price: number;
  quantity: number;
  image: any;
  supply_product_id?: string;
  business_b_supply_product_price_id?: string;
  is_default_quantity_required?: boolean;
  default_quantity_required?: number;
};

export type Product = {
  id: string;
  product_name: string;
  product_type: string;
  price: number;
  strength?: {
    strengthId: string;
    strengthName: string;
    sizeName: string;
    business_b_id: string;
    business_b_drug_price_id: string;
  };
  is_controlled_substance?: boolean;
  is_refrigerated?: boolean;
  days_supply?: number;
};

export type Prescription = {
  drugName: string;
  dateWritten: string;
  sig: string;
  authorizedRefills: string;
  daysSupply: string;
  qtySize: string;
  prescriptionNote: string;
  reasonToCompound: string;
  days_supply?: number;
};

export type CartOrder = {
  id: string;
  product: Product;
  quantity: number;
  supplies: Supply[];
  prescription?: Prescription;
  createdAt: number;
  status: 'draft' | 'pending' | 'complete';
};

type CartStore = {
  orders: CartOrder[];
  patientId?: string;
  patientDetails?: Record<string, any>;
  prescriberId?: string;
  prescriberDetails?: Record<string, any>;
  shipping?: Record<string, any>;
  deliveryNotes?: string;
  payBy?: string;

  addOrder: (product: Product) => string;
  updateOrder: (orderId: string, updates: Partial<CartOrder>) => void;
  updateProductQuantity: (orderId: string, quantity: number) => void;
  addSupplyToOrder: (orderId: string, supply: Supply) => void;
  updateSupplyQuantity: (
    orderId: string,
    supplyId: string,
    quantity: number
  ) => void;
  removeSupplyFromOrder: (orderId: string, supplyId: string) => void;
  setPrescriptionForOrder: (
    orderId: string,
    prescription: Prescription
  ) => void;
  setPatient: (patientId: string, patientDetails?: Record<string, any>) => void;
  setPrescriber: (prescriberDetails?: Record<string, any>) => void;
  setShipping: (shipping: Record<string, any>) => void;
  setDeliveryNotes: (deliveryNotes: string) => void;
  setPayBy: (payBy: string) => void;
  removeOrder: (orderId: string) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getOrderCount: () => number;
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      orders: [],
      patientId: undefined,
      patientDetails: undefined,
      prescriberId: undefined,
      prescriberDetails: undefined,
      shipping: undefined,
      deliveryNotes: undefined,

      addOrder: (product) => {
        const orderId = `order_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 9)}`;
        set((state) => ({
          orders: [
            ...state.orders,
            {
              id: orderId,
              product,
              quantity: 1,
              supplies: [],
              createdAt: Date.now(),
              status: 'draft',
            },
          ],
        }));
        return orderId;
      },

      updateOrder: (orderId, updates) => {
        set((state) => ({
          orders: state.orders.map((order) =>
            order.id === orderId ? { ...order, ...updates } : order
          ),
        }));
      },

      updateProductQuantity: (orderId, quantity) => {
        set((state) => ({
          orders: state.orders.map((order) =>
            order.id === orderId
              ? { ...order, quantity: Math.max(1, quantity) }
              : order
          ),
        }));
      },

      addSupplyToOrder: (orderId, supply) => {
        set((state) => ({
          orders: state.orders.map((order) => {
            if (order.id === orderId) {
              const existingSupplyIndex = order.supplies.findIndex(
                (s) => s.id === supply.id
              );

              if (existingSupplyIndex >= 0) {
                const updatedSupplies = [...order.supplies];
                updatedSupplies[existingSupplyIndex] = {
                  ...updatedSupplies[existingSupplyIndex],
                  quantity:
                    updatedSupplies[existingSupplyIndex].quantity +
                    supply.quantity,
                };
                return { ...order, supplies: updatedSupplies };
              } else {
                return {
                  ...order,
                  supplies: [...order.supplies, supply],
                };
              }
            }
            return order;
          }),
        }));
      },

      updateSupplyQuantity: (orderId, supplyId, quantity) => {
        set((state) => ({
          orders: state.orders.map((order) => {
            if (order.id === orderId) {
              const supply = order.supplies.find(s => s.id === supplyId);
              if (supply?.is_default_quantity_required && supply?.default_quantity_required) {
                const minQuantity = supply.default_quantity_required * order.quantity;
                if (quantity < minQuantity) {
                  return order;
                }
              }
              return {
                ...order,
                supplies: order.supplies.map((supply) =>
                  supply.id === supplyId
                    ? { ...supply, quantity: Math.max(1, quantity) }
                    : supply
                ),
              };
            }
            return order;
          }),
        }));
      },

      removeSupplyFromOrder: (orderId, supplyId) => {
        set((state) => ({
          orders: state.orders.map((order) => {
            if (order.id === orderId) {
              return {
                ...order,
                supplies: order.supplies.filter(
                  (supply) => supply.id !== supplyId
                ),
              };
            }
            return order;
          }),
        }));
      },

      setPrescriptionForOrder: (orderId, prescription) => {
        set((state) => ({
          orders: state.orders.map((order) =>
            order.id === orderId ? { ...order, prescription } : order
          ),
        }));
      },

      setPatient: (patientId, patientDetails) => {
        set({ patientId, patientDetails });
      },

      setPrescriber: (prescriberDetails) => {
        set({ prescriberDetails });
      },

      setShipping: (shipping) => {
        set({ shipping });
      },

      setDeliveryNotes: (deliveryNotes) => {
        set({ deliveryNotes });
      },

      setPayBy: (payBy) => {
        set({ payBy });
      },

      removeOrder: (orderId) => {
        set((state) => ({
          orders: state.orders.filter((order) => order.id !== orderId),
        }));
      },

      clearCart: () =>
        set({
          orders: [],
          patientId: undefined,
          patientDetails: undefined,
          prescriberId: undefined,
          prescriberDetails: undefined,
          shipping: undefined,
        }),

      getCartTotal: () => {
        const { orders } = get();
        return orders.reduce((total, order) => {
          let orderTotal = order.product.price * order.quantity || 0;

          orderTotal += order.supplies.reduce(
            (supplyTotal, supply) =>
              supplyTotal + supply.price * supply.quantity,
            0
          );

          return total + orderTotal;
        }, 0);
      },

      getOrderCount: () => get().orders.length,
    }),
    {
      name: 'cart-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
